/**
 * Aspect Ratio Conversion Types
 * 
 * Types for converting between aspect ratios (16:9 ↔ 9:16)
 * with media adjustments (position, scale)
 */

// ============================================================================
// ADJUSTMENT TYPES
// ============================================================================

/**
 * Position adjustment for media item
 * Values are percentages (-100 to 100)
 * 
 * @example
 * { x: 0, y: 0 }     // Centered (default)
 * { x: -50, y: 0 }   // Shift left (show right portion of content)
 * { x: 50, y: 0 }    // Shift right (show left portion of content)
 * { x: 0, y: -50 }   // Shift up (show bottom portion)
 * { x: 0, y: 50 }    // Shift down (show top portion)
 */
export interface MediaPosition {
  x: number; // Horizontal offset (-100 to 100, percentage)
  y: number; // Vertical offset (-100 to 100, percentage)
}

/**
 * Scale adjustment for media item
 * Values are scale factors (0.1 to 3.0)
 * 
 * @example
 * { scaleX: 1, scaleY: 1 }     // Original size (default)
 * { scaleX: 1.5, scaleY: 1.5 } // 150% zoom (bigger, less content visible)
 * { scaleX: 0.8, scaleY: 0.8 } // 80% zoom (smaller, more content visible)
 */
export interface MediaScale {
  scaleX: number; // Horizontal scale (0.1 to 3.0)
  scaleY: number; // Vertical scale (0.1 to 3.0)
}

/**
 * Complete adjustment data for a media item
 */
export interface MediaAdjustments {
  position?: MediaPosition;
  scale?: MediaScale;
}

// ============================================================================
// CONVERSION MODE TYPES
// ============================================================================

/**
 * Conversion mode state
 * - 'none': No conversion active
 * - '16:9-to-9:16': Converting landscape to portrait
 * - '9:16-to-16:9': Converting portrait to landscape
 */
export type ConversionMode = 'none' | '16:9-to-9:16' | '9:16-to-16:9';

/**
 * Conversion state information
 */
export interface ConversionState {
  isActive: boolean;
  mode: ConversionMode;
  sourceAspectRatio: string;
  targetAspectRatio: string;
}

// ============================================================================
// CROP CALCULATION TYPES
// ============================================================================

/**
 * Crop boundaries for aspect ratio conversion
 */
export interface CropBounds {
  x: number;      // X position in pixels
  y: number;      // Y position in pixels
  width: number;  // Crop width in pixels
  height: number; // Crop height in pixels
}

/**
 * Source and target resolution information
 */
export interface ResolutionInfo {
  source: {
    width: number;
    height: number;
    aspectRatio: string;
  };
  target: {
    width: number;
    height: number;
    aspectRatio: string;
  };
}

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Default adjustments for new media items
 */
export const DEFAULT_ADJUSTMENTS: MediaAdjustments = {
  position: { x: 0, y: 0 },
  scale: { scaleX: 1, scaleY: 1 }
};

/**
 * Adjustment boundaries
 */
export const ADJUSTMENT_LIMITS = {
  position: {
    min: -100,
    max: 100
  },
  scale: {
    min: 0.1,
    max: 3.0
  }
} as const;

/**
 * Supported aspect ratio conversions
 */
export const SUPPORTED_CONVERSIONS = {
  '16:9': ['9:16', '1:1'],
  '9:16': ['16:9', '1:1'],
  '1:1': ['16:9', '9:16']
} as const;
